import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { getPlatformKey } from 'services/Utilities'

import BaseModal from 'components/Modals/BaseModal'
import { Card, List } from 'ui'

interface ShortCut {
  macOS: Array<string> | Array<object>
  windows: Array<string>
  separator: string
  cursor?: Array<string>
}

interface ScheduleElement {
  type: string
  label: string
  icon?: string
  color?: string
  shortCut?: ShortCut
  helpName: string
  helpPriority?: number
}

const SheduleIcon: FC<Pick<ScheduleElement, 'icon' | 'helpName'>> = icon => {
  return (
    <List.Item className="d-flex align-items-center p-1">
      <i className={`fas ${icon.icon} fa-lg m-2`}></i>
      <small className="text-nowrap">{icon.helpName}</small>
    </List.Item>
  )
}

const ShiftColor: FC<Pick<ScheduleElement, 'color' | 'type' | 'label' | 'helpName' | 'icon'>> = color => {
  const { t } = useTranslation()
  const userName = color.type === 'unassigned-shift' ? t('manager.schedule.LEGEND.UNASSIGNED_SHIFT_SAMPLE') : 'John Doe'
  const absenceReason =
    color.type === 'assigned-employe-status' && color.label !== 'replacement' ? ' / ' + t(`manager.schedule.LEGEND.SHIFT_SAMPLE.${color.label}`) : null
  return (
    <div className="d-flex flex-column p-1">
      <small>
        <small className="d-flex text-nowrap ml-3">{color.helpName}</small>
      </small>
      <div className={`d-flex merinio_scheduler_event merinio-scheduler-event-${color.color} flex-even ml-3 mr-2 pr-0`}>
        <div className="d-flex flex-column pl-2 pr-2">
          <small>
            {color.icon ? <i className={`fas ${color.icon} fa-xs mr-1`}></i> : null}
            <small>{userName}</small>
          </small>
          <small className="text-nowrap">
            <small className="font-weight-bold">09:00 - 17:00</small>
            <small className="text-nowrap">{absenceReason}</small>
          </small>
        </div>
      </div>
    </div>
  )
}

const TagColor: FC<Pick<ScheduleElement, 'color' | 'type' | 'label' | 'helpName' | 'icon'>> = color => {
  const { t } = useTranslation()
  const userName = color.type === 'unassigned-shift' ? t('manager.schedule.LEGEND.UNASSIGNED_SHIFT_SAMPLE') : 'John Doe'

  return (
    <div className="d-flex flex-column p-1">
      <small>
        <small className="d-flex ml-3">{color.helpName}</small>
      </small>
      <div className={`d-flex merinio_scheduler_event merinio-scheduler-event-right-border-${color.color} flex-even ml-3 mr-2`}>
        <div className="d-flex flex-column pl-2 pr-2">
          <small>
            <small>{userName}</small>
          </small>
          <small className="text-nowrap">
            <small className="font-weight-bold">09:00 - 17:00</small>
            <small className="text-nowrap"> / {t(`manager.schedule.LEGEND.SHIFT_SAMPLE.${color.label}`)}</small>
          </small>
        </div>
      </div>
    </div>
  )
}

const ScheduleShortCut: FC<Pick<ScheduleElement, 'helpName' | 'type' | 'label' | 'shortCut'>> = shortC => {
  const shortCut = getPlatformKey() === 'MAC' ? shortC.shortCut?.macOS : shortC.shortCut?.windows

  return (
    <List.Item className="d-flex justify-content-between align-items-center pb-2 pt-2">
      <div className="d-flex align-items-center mr-2">
        <small>{shortC.helpName}</small>
      </div>
      <div className="d-flex row nowrap align-items-center">
        {shortCut?.map((symbol, index) => {
          return (
            <div key={`${symbol}-${index}`} className="d-flex row flex-nowrap">
              {typeof symbol === 'string' ? <span className="border-secondary rounded pl-2 pr-2 p-1 small">{symbol}</span> : null}
              {index === 0 ? <span className="ml-2 mr-3">{shortC.shortCut?.separator}</span> : null}
              {shortC.shortCut?.cursor ? (
                <div className="d-flex row flex-nowrap align-items-center">
                  <i className={`${shortC.shortCut.cursor}`}></i>
                  <i className="fas fa-mouse-pointer ml-2 mr-1"></i>
                </div>
              ) : null}
            </div>
          )
        })}
      </div>
    </List.Item>
  )
}

const ScheduleHelpModal: FC = () => {
  const { t } = useTranslation()

  const sheduleStatus = [
    {
      type: 'shift-state-icon',
      label: 'published-shift',
      icon: 'fa-check-square',
      helpName: t('manager.schedule.LEGEND.SHIFT_STATE.PUBLISHED'),
      helpPriority: 0
    },
    {
      type: 'shift-state-icon',
      label: 'notified-shift',
      icon: 'fa-envelope',
      helpName: t('manager.schedule.LEGEND.SHIFT_STATE.NOTIFIED'),
      helpPriority: 0
    },
    {
      type: 'shift-state-icon',
      label: 'locked-shift',
      icon: 'fa-lock',
      helpName: t('manager.schedule.LEGEND.SHIFT_STATE.LOCKED'),
      helpPriority: 0
    },
    {
      type: 'shift-state-icon',
      label: 'confirmed-shift',
      icon: 'fa-user-check',
      helpName: t('manager.schedule.LEGEND.SHIFT_STATE.CONFIRMED'),
      helpPriority: 1
    },
    {
      type: 'punchCard-state-icon',
      label: 'punch-in',
      icon: 'fa-sign-in-alt',
      helpName: t('manager.schedule.LEGEND.PUNCH_IN'),
      helpPriority: 1
    },
    {
      type: 'punchCard-state-icon',
      label: 'punch-out',
      icon: 'fa-sign-out-alt',
      helpName: t('manager.schedule.LEGEND.PUNCH_OUT'),
      helpPriority: 1
    },
    {
      type: 'assigned-employe-status',
      label: 'ABSENT',
      icon: 'fa-unlink',
      helpName: t('manager.schedule.LEGEND.ABSENCE_REASONS.ABSENT'),
      helpPriority: 2
    },
    {
      type: 'assigned-employe-status',
      label: 'LATE_EARLY_OUT',
      icon: 'fa-alarm-clock',
      helpName: t('manager.schedule.LEGEND.ABSENCE_REASONS.LATE_EARLY_OUT'),
      helpPriority: 2
    },
    {
      type: 'assigned-employe-status',
      label: 'TIME_OFF',
      icon: 'fa-plane',
      helpName: t('manager.schedule.LEGEND.ABSENCE_REASONS.TIME_OFF'),
      helpPriority: 2
    },
    {
      type: 'notification-state-icon',
      label: 'opened-notification',
      icon: 'fa-envelope-open',
      helpName: t('manager.schedule.LEGEND.OPENED_NOTIF'),
      helpPriority: 3
    },
    {
      type: 'shift-state-icon',
      label: 'commented-shift',
      icon: 'fa-comment',
      helpName: t('manager.schedule.LEGEND.SHIFT_STATE.COMMENTED'),
      helpPriority: 3
    },
    {
      type: 'shift-state-icon',
      label: 'contactList',
      icon: 'fa-phone',
      helpName: t('manager.schedule.LEGEND.SHIFT_STATE.CONTACTLIST'),
      helpPriority: 3
    }
  ]

  const planification = [
    {
      type: 'unassigned-shift',
      label: 'ACTION_REQUIRED',
      color: 'green merinio-scheduler-event-gray-dark event-striped',
      helpName: t('manager.schedule.LEGEND.UNASSIGNED_SHIFT_TAGS.ACTION_REQUIRED'),
      helpPriority: 1
    },
    {
      type: 'unassigned-shift',
      label: 'NO_ACTIONS',
      color: 'yellow merinio-scheduler-event-gray-dark event-striped',
      helpName: t('manager.schedule.LEGEND.UNASSIGNED_SHIFT_TAGS.NO_ACTIONS'),
      helpPriority: 1
    },
    {
      type: 'assigned-shift',
      label: 'ATENTION_MENTIONNED',
      color: 'yellow merinio-scheduler-event-gray-light',
      helpName: t('manager.schedule.LEGEND.ATENTION_MENTIONNED'),
      helpPriority: 2
    }
  ]

  const operations = [
    {
      type: 'assigned-employe-status',
      label: 'ABSENT',
      color: 'red',
      icon: 'fa-unlink',
      helpName: t('manager.schedule.LEGEND.ABSENCE_REASONS.ABSENT'),
      helpPriority: 0
    },
    {
      type: 'assigned-employe-status',
      label: 'LATE_EARLY_OUT',
      color: 'yellow',
      icon: 'fa-alarm-clock',
      helpName: t('manager.schedule.LEGEND.ABSENCE_REASONS.LATE_EARLY_OUT'),
      helpPriority: 0
    },
    {
      type: 'assigned-employe-status',
      label: 'TIME_OFF',
      color: 'purple',
      icon: 'fa-plane',
      helpName: t('manager.schedule.LEGEND.ABSENCE_REASONS.TIME_OFF'),
      helpPriority: 0
    },
    {
      type: 'assigned-employe-status',
      label: 'replacement',
      color: 'green',
      helpName: t('manager.schedule.LEGEND.ABSENCE_REASONS.REPLACEMENT'),
      helpPriority: 0
    }
  ]

  const pay = [
    {
      type: 'pay-status',
      label: 'OVER_TIME',
      color: 'red merinio-scheduler-event-gray-light',
      helpName: t('manager.schedule.LEGEND.PAY_AFFECT.OVER_TIME'),
      helpPriority: 2
    },
    {
      type: 'pay-status',
      label: 'ON_CALL',
      color: 'blue merinio-scheduler-event-gray-light',
      helpName: t('manager.schedule.LEGEND.PAY_AFFECT.ON_CALL'),
      helpPriority: 2
    }
  ]

  const allPeriods = [
    {
      type: 'selected-element',
      label: 'assigned-shift',
      color: 'gray-dark merinio_scheduler_selected',
      helpName: t('manager.schedule.LEGEND.SELECTED_SHIFT'),
      helpPriority: 1
    },
    {
      type: 'unassigned-shift',
      label: 'UNASSIGNED_SHIFT',
      color: 'gray-dark event-striped',
      helpName: t('manager.schedule.LEGEND.UNASSIGNED_SHIFT'),
      helpPriority: 1
    }
  ]

  const sheduleActions = [
    {
      type: 'shift-selection-shortCut',
      label: 'select-all',
      shortCut: {
        macOS: ['⌘', 'a'],
        windows: ['Ctrl', 'a'],
        separator: '+'
      },
      helpName: t('manager.schedule.LEGEND.SHORTCUT_ACTIONS.SELECT_ALL'),
      helpPriority: 0
    },
    {
      type: 'shift-selection-shortCut',
      label: 'select-some',
      shortCut: {
        macOS: ['cmd'],
        windows: ['Ctrl'],
        separator: '+',
        cursor: ['fa-solid fa-sparkles']
      },
      helpName: t('manager.schedule.LEGEND.SHORTCUT_ACTIONS.SELECT_SOME'),
      helpPriority: 0
    },
    {
      type: 'shift-selection-shortCut',
      label: 'select-many',
      shortCut: {
        macOS: ['shift'],
        windows: ['Shift'],
        separator: '+',
        cursor: ['fa-light fa-square-dashed']
      },
      helpName: t('manager.schedule.LEGEND.SHORTCUT_ACTIONS.SELECT_MANY'),
      helpPriority: 0
    },
    {
      type: 'selectedShift-actions-shortCut',
      label: 'copy-selection',
      shortCut: {
        macOS: ['⌘', 'c'],
        windows: ['Ctrl', 'c'],
        separator: '+'
      },
      helpName: t('manager.schedule.LEGEND.SHORTCUT_ACTIONS.COPY'),
      helpPriority: 0
    },
    {
      type: 'selectedShift-actions-shortCut',
      label: 'paste-selection',
      shortCut: {
        macOS: ['⌘', 'v'],
        windows: ['Ctrl', 'v'],
        separator: '+'
      },
      helpName: t('manager.schedule.LEGEND.SHORTCUT_ACTIONS.PASTE'),
      helpPriority: 0
    },
    {
      type: 'filter-navigation-shortCut',
      label: 'previous-next',
      shortCut: {
        macOS: ['▲', '▼'],
        windows: ['↑', '↓'],
        separator: ''
      },
      helpName: t('manager.schedule.LEGEND.SHORTCUT_ACTIONS.FILTER_NAV'),
      helpPriority: 1
    },
    {
      type: 'filter-navigation-shortCut',
      label: 'add-pull',
      shortCut: {
        macOS: ['⮑'],
        windows: ['⮑'],
        separator: ''
      },
      helpName: t('manager.schedule.LEGEND.SHORTCUT_ACTIONS.ADD_FILTER'),
      helpPriority: 1
    },
    {
      type: 'filter-navigation-shortCut',
      label: 'escape',
      shortCut: {
        macOS: ['esc'],
        windows: ['esc'],
        separator: ''
      },
      helpName: t('manager.schedule.LEGEND.SHORTCUT_ACTIONS.ESCAPE'),
      helpPriority: 1
    },
    {
      type: 'search-actions-shortCut',
      label: 'search',
      shortCut: {
        macOS: ['⌘', '⮑'],
        windows: ['Ctrl', '⮑'],
        separator: '+'
      },
      helpName: t('manager.schedule.LEGEND.SHORTCUT_ACTIONS.SEARCH'),
      helpPriority: 1
    }
  ]

  const makeColumns = (length: number) => new Array(length).fill(0)

  return (
    <BaseModal>
      <h4>{t('manager.schedule.LEGEND.TITLE')}</h4>
      <Card className="p-3 mt-1">
        <Card.Title className="text-center">
          <h6>{t('manager.schedule.LEGEND.SHIFT_SECTION_TITLE')}</h6>
        </Card.Title>
        <div className="row mb-3">
          {makeColumns(4).map((_, index) => {
            return (
              <List className="col-sm" key={index}>
                {sheduleStatus
                  .filter(status => status.helpPriority === index)
                  .map(status => (
                    <SheduleIcon key={`${status.type}-${status.label}`} icon={status.icon} helpName={status.helpName} />
                  ))}
              </List>
            )
          })}
        </div>
        <div className="row mr-1 ml-1">
          {makeColumns(3).map((_, index) => {
            return (
              <div className="col-sm" key={index}>
                {allPeriods
                  .filter(status => status.helpPriority === index)
                  .map(status => (
                    <ShiftColor
                      key={`${status.type}-${status.label}`}
                      color={status.color}
                      type={status.type}
                      label={status.label}
                      helpName={status.helpName}
                    />
                  ))}
                {planification
                  .filter(status => status.helpPriority === index)
                  .map(status => (
                    <TagColor key={`${status.type}-${status.label}`} color={status.color} type={status.type} label={status.label} helpName={status.helpName} />
                  ))}
                {operations
                  .filter(status => status.helpPriority === index)
                  .map(status => (
                    <ShiftColor
                      key={`${status.type}-${status.label}`}
                      color={status.color}
                      type={status.type}
                      label={status.label}
                      helpName={status.helpName}
                      icon={status.icon}
                    />
                  ))}
                {pay
                  .filter(status => status.helpPriority === index)
                  .map(status => (
                    <TagColor key={`${status.type}-${status.label}`} color={status.color} type={status.type} label={status.label} helpName={status.helpName} />
                  ))}
              </div>
            )
          })}
        </div>
      </Card>
      <Card className="mt-4 p-3 mb-5">
        <Card.Title className="text-center m-2">
          <h6>{t('manager.schedule.LEGEND.SHORTCUTS_SECTION_TITLE')}</h6>
        </Card.Title>
        <div className="row">
          {makeColumns(2).map((_, index) => {
            return (
              <List className="col-sm" key={index}>
                {sheduleActions
                  .filter(action => action.helpPriority === index)
                  .map(action => (
                    <ScheduleShortCut
                      key={`${action.type}-${action.label}`}
                      shortCut={action.shortCut}
                      helpName={action.helpName}
                      type={action.type}
                      label={action.label}
                    />
                  ))}
              </List>
            )
          })}
        </div>
      </Card>
    </BaseModal>
  )
}

export default ScheduleHelpModal
